import {
  AppstoreOutlined,
  AudioOutlined,
  CloseOutlined,
  CloudOutlined,
  DeploymentUnitOutlined,
  FileTextOutlined,
  IdcardOutlined,
  MessageOutlined,
  MoreOutlined,
  PauseCircleFilled,
  PlayCircleFilled,
  ReloadOutlined,
  SearchOutlined,
  ThunderboltOutlined,
  CustomerServiceOutlined,
  FilterOutlined,
  FolderAddOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import { Button, Checkbox, Dropdown, Empty, Input, Modal, Pagination, Popover, Select, message } from 'antd'
import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import {
  listAudioAssets,
  listCanvasMyVoiceTemplates,
  uploadAudioAsset,
  type SaasApiError,
  type AudioAssetItem,
  type AudioAssetOwnership,
  type AudioAssetSource,
  type AudioAssetSummary,
  type AudioPresentationType,
} from '../../api'
import { buildAudioUploadFileInputAccept, isAllowedAudioAssetUploadFile } from '../../audioUploadAllowlist'
import {
  filterSessionScopedNewPickerIds,
  isMobileCaptureSessionPaired,
  type MobileCaptureSessionMeta,
} from '../../hooks/mobileCapturePickerCompletion'
import type { MobileAudioPresentationType } from '../../mobileCaptureApi'
import AudioWaveformPlaceholder from '../../workspace/AudioWaveformPlaceholder'
import AssetUploadActionTile from '../AssetUploadActionTile/AssetUploadActionTile'
import MobileCaptureQrModal from '../MobileCaptureQrModal'
import {
  AUDIO_PICKER_DEFAULT_PAGE_SIZE,
  AUDIO_PICKER_DISPLAY_MODE,
  failedVoiceTemplateTooltipKey,
  isAudioAssetSelectable,
  presentationTypeFromCategoryKey,
  resolveInitialCategory,
  resolvePickerCategories,
  summaryCountForCategory,
  voiceTemplateListItemToAudioAssetItem,
  voiceTemplatePickerSummaryFromTotal,
  type AudioAssetPickerDomain,
  type AudioPickerCategoryKey,
} from './audioAssetPickerContract'
import ImageFolderBreadcrumb from '../ImageFolderTree/ImageFolderBreadcrumb'
import ImageFolderTree from '../ImageFolderTree/ImageFolderTree'
import FolderNameModal, { type FolderNameModalState } from '../ImageFolderTree/FolderNameModal'
import {
  teamQueryForScope,
  uploadTargetForScope,
  scopeAfterLocalUpload,
  type PickerTeamScope,
} from '../ImageFolderTree/folderTreeModel'
import { useAudioPickerFolders } from './useAudioPickerFolders'
import '../ImageAssetPicker/image-asset-picker.css'
import './audio-asset-picker.css'

const PAGE_SIZE_OPTIONS = [20, 50, 100]
const MOBILE_CAPTURE_POLL_MS = 2000

const TAB_ICON: Record<AudioPickerCategoryKey, ReactNode> = {
  all: <AppstoreOutlined />,
  voice_template: <IdcardOutlined />,
  speech: <MessageOutlined />,
  music: <CustomerServiceOutlined />,
  sfx: <ThunderboltOutlined />,
  ambience: <CloudOutlined />,
  recording: <AudioOutlined />,
  reference_audio: <FileTextOutlined />,
  mixdown: <DeploymentUnitOutlined />,
}

function formatDurationMs(ms: number | null): string {
  if (ms == null || Number.isNaN(ms) || ms <= 0) return '—'
  const totalSeconds = Math.round(ms / 1000)
  const m = Math.floor(totalSeconds / 60)
  const s = totalSeconds % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

function formatCreatedAt(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  const y = d.getFullYear()
  const mo = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  return `${y}-${mo}-${day} ${hh}:${mm}`
}

export interface AudioAssetPickerProps {
  open: boolean
  workspaceId: string
  /** Canvas project id — required when `pickerDomain` is `voice_template`. */
  projectId?: string
  /** Shared shell, separate datasource — VistoeraTASK-00123. */
  pickerDomain?: AudioAssetPickerDomain
  title?: string
  /** Preset category — Generate Audio voice-template entry uses VOICE_TEMPLATE. */
  presentationType?: AudioPresentationType
  /** When true, only `presentationType` / `allowedCategories` are shown. */
  lockPresentationType?: boolean
  allowedCategories?: AudioPresentationType[]
  initialCategory?: AudioPresentationType | 'all'
  selectionMode?: 'single' | 'multiple'
  initialSelection?: string | null
  /** When true, show explicit local-upload action (opens OS file picker). */
  enableLocalUpload?: boolean
  onCancel: () => void
  onConfirm: (item: AudioAssetItem) => void
}

type UploadablePresentationType = Exclude<AudioPresentationType, 'VOICE_TEMPLATE' | 'SPEECH' | 'MIXDOWN'>

function uploadPresentationForCategory(key: AudioPickerCategoryKey): UploadablePresentationType {
  if (key === 'music') return 'MUSIC'
  if (key === 'sfx') return 'SFX'
  if (key === 'ambience') return 'AMBIENCE'
  if (key === 'recording') return 'RECORDING'
  return 'REFERENCE_AUDIO'
}

export default function AudioAssetPicker({
  open,
  workspaceId,
  projectId,
  pickerDomain = 'audio_asset',
  title,
  presentationType = 'VOICE_TEMPLATE',
  lockPresentationType = true,
  allowedCategories,
  initialCategory,
  selectionMode = 'single',
  initialSelection = null,
  enableLocalUpload = false,
  onCancel,
  onConfirm,
}: AudioAssetPickerProps) {
  const { t } = useTranslation(['canvas', 'audioAssets', 'actions', 'imageAssets'])
  const isVoiceTemplateDomain = pickerDomain === 'voice_template'
  const copyNs = isVoiceTemplateDomain ? 'voiceTemplatePicker' : 'audioAssetPicker'
  const modalTitle = title ?? t(`canvas:${copyNs}.defaultTitle`)

  const sidebarCategories = useMemo(
    () => resolvePickerCategories(allowedCategories, lockPresentationType, presentationType),
    [allowedCategories, lockPresentationType, presentationType],
  )

  const defaultCategory = useMemo(
    () =>
      resolveInitialCategory(initialCategory, presentationType, allowedCategories, lockPresentationType),
    [allowedCategories, initialCategory, lockPresentationType, presentationType],
  )

  const [tab, setTab] = useState<'library' | 'external'>('library')
  const [activeCategory, setActiveCategory] = useState<AudioPickerCategoryKey>(defaultCategory)
  const [items, setItems] = useState<AudioAssetItem[]>([])
  const [total, setTotal] = useState(0)
  const [summary, setSummary] = useState<AudioAssetSummary | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [searchDraft, setSearchDraft] = useState('')
  const [search, setSearch] = useState('')
  const [sourceFilter, setSourceFilter] = useState<AudioAssetSource | 'all'>('all')
  const [ownershipFilter, setOwnershipFilter] = useState<AudioAssetOwnership | 'all'>('all')
  const [sort, setSort] = useState<'recent' | 'oldest' | 'name' | 'duration'>('recent')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(AUDIO_PICKER_DEFAULT_PAGE_SIZE)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [activeAssetId, setActiveAssetId] = useState<string | null>(null)
  const [playingId, setPlayingId] = useState<string | null>(null)
  const [playbackProgress, setPlaybackProgress] = useState(0)
  const [playbackDuration, setPlaybackDuration] = useState(0)
  const searchTimer = useRef<number | null>(null)
  const audioElRef = useRef<HTMLAudioElement | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const openInitializedRef = useRef(false)
  const loadAbortRef = useRef<AbortController | null>(null)
  const [uploading, setUploading] = useState(false)
  const [mobileCaptureOpen, setMobileCaptureOpen] = useState(false)
  const [mobileSessionMeta, setMobileSessionMeta] = useState<MobileCaptureSessionMeta | null>(null)
  const mobileSessionMetaRef = useRef<MobileCaptureSessionMeta | null>(null)
  const mobilePollGenerationRef = useRef(0)
  const mobileCompletionSessionRef = useRef<string | null>(null)
  const knownAudioIdsRef = useRef<Set<string>>(new Set())

  const folderApi = useAudioPickerFolders(workspaceId, open && !isVoiceTemplateDomain)
  const teamEnabled = folderApi.teamEnabled && !isVoiceTemplateDomain
  const [scope, setScope] = useState<PickerTeamScope>({ kind: 'my_all' })
  const [folderModal, setFolderModal] = useState<FolderNameModalState | null>(null)
  const teamQuery = teamEnabled ? teamQueryForScope(scope) : null
  const teamQueryKey = teamQuery ? JSON.stringify(teamQuery) : ''
  const teamQueryRef = useRef(teamQuery)
  teamQueryRef.current = teamQuery

  const listType = presentationTypeFromCategoryKey(activeCategory)
  const uploadPresentationType = uploadPresentationForCategory(activeCategory)
  const mobileUploadReady = Boolean(workspaceId)
  const mobileAudioPresentationType = uploadPresentationType as MobileAudioPresentationType

  useEffect(() => {
    mobileSessionMetaRef.current = mobileSessionMeta
  }, [mobileSessionMeta])

  const stopPlayback = useCallback(() => {
    audioElRef.current?.pause()
    setPlayingId(null)
    setPlaybackProgress(0)
    setPlaybackDuration(0)
  }, [])

  const loadGallery = useCallback(async () => {
    if (!workspaceId) return
    loadAbortRef.current?.abort()
    const controller = new AbortController()
    loadAbortRef.current = controller
    setLoading(true)
    setError(null)
    try {
      if (isVoiceTemplateDomain) {
        if (!projectId) throw new Error('projectId is required for voice template picker')
        const { data } = await listCanvasMyVoiceTemplates(workspaceId, projectId, {
          q: search.trim() || undefined,
          sort: sort === 'duration' ? 'recent' : sort,
          page,
          page_size: pageSize,
          include_unusable: true,
        })
        if (controller.signal.aborted) return
        const mapped = data.items.map(voiceTemplateListItemToAudioAssetItem)
        setItems(mapped)
        setTotal(data.total ?? data.count)
        setSummary(voiceTemplatePickerSummaryFromTotal(data.total ?? data.count))
        return
      }
      const teamScopeQuery = teamQueryRef.current
      const { data } = await listAudioAssets(workspaceId, {
        q: search.trim() || undefined,
        type: listType,
        source: sourceFilter === 'all' ? undefined : sourceFilter,
        ownership: ownershipFilter === 'all' ? undefined : ownershipFilter,
        sort,
        page,
        page_size: pageSize,
        ...(teamScopeQuery ?? {}),
      })
      if (controller.signal.aborted) return
      setItems(data.items)
      setTotal(data.total)
      setSummary(data.summary)
    } catch (e) {
      if (controller.signal.aborted) return
      setItems([])
      setTotal(0)
      setError((e as Error).message || t(`canvas:${copyNs}.loadFailed`))
    } finally {
      if (!controller.signal.aborted) setLoading(false)
    }
  }, [
    copyNs,
    isVoiceTemplateDomain,
    listType,
    ownershipFilter,
    page,
    pageSize,
    projectId,
    search,
    sort,
    sourceFilter,
    t,
    teamQueryKey,
    workspaceId,
  ])

  const handleLocalUploadPick = useCallback(() => {
    if (uploading) return
    fileInputRef.current?.click()
  }, [uploading])

  const openMobileCapture = useCallback(() => {
    if (!mobileUploadReady) {
      message.error(t('canvas:mediaCard.picker.upload.mobileUnavailable'))
      return
    }
    if (mobileCaptureOpen) return
    mobilePollGenerationRef.current += 1
    mobileCompletionSessionRef.current = null
    knownAudioIdsRef.current = new Set()
    setMobileSessionMeta(null)
    setMobileCaptureOpen(true)
  }, [mobileCaptureOpen, mobileUploadReady, t])

  const finalizeUploadedAudio = useCallback(
    async (uploadedId: string) => {
      setSort('recent')
      setSearch('')
      setSearchDraft('')
      setPage(1)
      setSelectedId(uploadedId)
      setActiveAssetId(uploadedId)
      await loadGallery()
      message.success(t('canvas:audioAssetPicker.uploadSuccess'))
    },
    [loadGallery, t],
  )

  const handleLocalUploadFile = useCallback(
    async (file: File) => {
      if (!workspaceId || uploading) return
      if (!isAllowedAudioAssetUploadFile(file)) {
        message.error(t('audioAssets:uploadModal.unsupportedFormat'))
        return
      }
      setUploading(true)
      try {
        const uploadCollectionId = teamEnabled ? uploadTargetForScope(scope) : null
        const { data } = await uploadAudioAsset(
          workspaceId,
          file,
          uploadPresentationType,
          file.name.replace(/\.[^/.]+$/, ''),
          undefined,
          uploadCollectionId ? { collection_id: uploadCollectionId } : undefined,
        )
        if (teamEnabled) setScope(scopeAfterLocalUpload(scope))
        const newId = data.id || data.asset_id
        setPage(1)
        setSearch('')
        setSearchDraft('')
        const { data: listData } = await listAudioAssets(workspaceId, {
          type: uploadPresentationType,
          sort: 'recent',
          page: 1,
          page_size: pageSize,
        })
        setItems(listData.items)
        setTotal(listData.total)
        setSummary(listData.summary)
        if (newId) {
          setSelectedId(newId)
          setActiveAssetId(newId)
        }
        message.success(t('canvas:audioAssetPicker.uploadSuccess'))
      } catch (e) {
        message.error((e as Error).message || t('audioAssets:uploadModal.failed'))
      } finally {
        setUploading(false)
      }
    },
    [scope, teamEnabled, t, uploadPresentationType, uploading, workspaceId, pageSize],
  )

  useEffect(() => {
    if (!open) {
      openInitializedRef.current = false
      stopPlayback()
      return
    }
    if (openInitializedRef.current) return
    openInitializedRef.current = true
    setTab('library')
    setActiveCategory(defaultCategory)
    setSelectedId(initialSelection)
    setActiveAssetId(initialSelection)
    setPage(1)
    setSearch('')
    setSearchDraft('')
    setSourceFilter('all')
    setOwnershipFilter('all')
    setSort('recent')
    setPageSize(AUDIO_PICKER_DEFAULT_PAGE_SIZE)
    setItems([])
    setSummary(null)
    setError(null)
    setScope({ kind: 'my_all' })
  }, [defaultCategory, initialSelection, open, stopPlayback])

  useEffect(() => {
    if (!open || tab !== 'library' || !openInitializedRef.current) return
    void loadGallery()
  }, [loadGallery, open, tab])

  useEffect(() => {
    if (!open || !mobileCaptureOpen || tab !== 'library') return
    if (!isMobileCaptureSessionPaired(mobileSessionMeta)) return

    const generation = mobilePollGenerationRef.current
    const activeSessionId = mobileSessionMeta!.sessionId
    const sessionCreatedAt = mobileSessionMeta!.createdAt
    let baselineReady = false
    let cancelled = false

    const tick = async () => {
      if (cancelled || generation !== mobilePollGenerationRef.current) return
      const meta = mobileSessionMetaRef.current
      if (!meta || meta.sessionId !== activeSessionId) return
      if (mobileCompletionSessionRef.current === activeSessionId) return

      let pollItems: AudioAssetItem[] = []
      try {
        const { data } = await listAudioAssets(workspaceId, {
          sort: 'recent',
          page: 1,
          page_size: pageSize,
        })
        pollItems = data.items
      } catch {
        return
      }
      if (cancelled || generation !== mobilePollGenerationRef.current) return
      if (mobileSessionMetaRef.current?.sessionId !== activeSessionId) return

      if (!baselineReady) {
        knownAudioIdsRef.current = new Set(pollItems.map((item) => item.id))
        baselineReady = true
        return
      }

      const newIds = filterSessionScopedNewPickerIds(pollItems, knownAudioIdsRef.current, sessionCreatedAt)
      if (newIds.length === 0) return
      if (mobileCompletionSessionRef.current === activeSessionId) return

      newIds.forEach((id) => knownAudioIdsRef.current.add(id))
      mobileCompletionSessionRef.current = activeSessionId
      await finalizeUploadedAudio(newIds[newIds.length - 1])
      setMobileCaptureOpen(false)
    }

    const timer = window.setInterval(() => {
      void tick()
    }, MOBILE_CAPTURE_POLL_MS)
    void tick()
    return () => {
      cancelled = true
      window.clearInterval(timer)
    }
  }, [finalizeUploadedAudio, mobileCaptureOpen, mobileSessionMeta, open, pageSize, tab, workspaceId])

  useEffect(() => {
    if (!open) stopPlayback()
  }, [open, stopPlayback])

  const onSearchChange = (value: string) => {
    setSearchDraft(value)
    if (searchTimer.current) window.clearTimeout(searchTimer.current)
    searchTimer.current = window.setTimeout(() => {
      setSearch(value)
      setPage(1)
      stopPlayback()
    }, 300)
  }

  const selected = useMemo(
    () => items.find((item) => item.id === selectedId) || null,
    [items, selectedId],
  )

  const detailItem = useMemo(() => {
    if (!activeAssetId) return null
    return items.find((item) => item.id === activeAssetId) || selected
  }, [activeAssetId, items, selected])

  const handleClose = useCallback(() => {
    loadAbortRef.current?.abort()
    stopPlayback()
    setSelectedId(null)
    setActiveAssetId(null)
    onCancel()
  }, [onCancel, stopPlayback])

  const handleConfirm = () => {
    if (!selected || !isAudioAssetSelectable(selected)) return
    stopPlayback()
    onConfirm(selected)
  }

  const togglePlay = useCallback(
    (item: AudioAssetItem, e?: React.MouseEvent) => {
      e?.stopPropagation()
      if (!item.has_preview_audio || !item.playback_url) return
      if (playingId === item.id) {
        stopPlayback()
        return
      }
      stopPlayback()
      setPlayingId(item.id)
      const el = audioElRef.current
      if (!el) return
      el.src = item.playback_url
      void el.play().catch(() => setPlayingId(null))
    },
    [playingId, stopPlayback],
  )

  const selectRow = useCallback(
    (item: AudioAssetItem) => {
      setActiveAssetId(item.id)
      if (!isAudioAssetSelectable(item)) return
      if (selectionMode === 'single') {
        setSelectedId(item.id)
      }
    },
    [selectionMode],
  )

  const onCategoryClick = (key: AudioPickerCategoryKey) => {
    setActiveCategory(key)
    setPage(1)
    stopPlayback()
  }

  const emptyDescription = useMemo(() => {
    if (isVoiceTemplateDomain) return t('canvas:voiceTemplatePicker.empty')
    if (activeCategory === 'voice_template' || (lockPresentationType && presentationType === 'VOICE_TEMPLATE')) {
      return t('canvas:audioAssetPicker.emptyVoiceTemplates')
    }
    return t('canvas:audioAssetPicker.emptyAssets')
  }, [activeCategory, isVoiceTemplateDomain, lockPresentationType, presentationType, t])

  const confirmLabel =
    selectedId && isAudioAssetSelectable(selected)
      ? t(`canvas:${copyNs}.confirmSelectCount`, { count: 1 })
      : t(`canvas:${copyNs}.confirmSelect`)

  const currentFolderId = scope.kind === 'folder' ? scope.folderId : null
  const scopeCounts = folderApi.data?.scope_counts ?? null
  const teamInfo = folderApi.data?.team
  const teamWorkspaceName = teamInfo?.workspace_name || ''
  const teamIsManager = teamInfo?.role === 'owner' || teamInfo?.role === 'admin'

  const selectScope = (next: PickerTeamScope) => {
    setScope(next)
    setPage(1)
    stopPlayback()
  }
  const navigateFolder = (folderId: string | null) =>
    selectScope(folderId ? { kind: 'folder', folderId } : { kind: 'my_all' })

  const scopeRootLabel =
    scope.kind === 'my_created'
      ? t('imageAssets:folders.scope.myCreated')
      : scope.kind === 'team_all'
        ? t('imageAssets:folders.scope.teamShared')
        : scope.kind === 'team_members'
          ? t('imageAssets:folders.scope.teamMembers')
          : scope.kind === 'unsorted'
            ? t('audioAssets:folders.scope.unsortedAudio')
            : t('audioAssets:folders.scope.root')

  const scopeEntry = (key: PickerTeamScope['kind'], label: string, count: number | undefined) => (
    <button
      key={key}
      type="button"
      className={`iap-filter-item ${scope.kind === key ? 'active' : ''}`}
      onClick={() => selectScope({ kind: key } as PickerTeamScope)}
      data-testid={`aap-scope-${key}`}
    >
      <span>{label}</span>
      <span className="iap-filter-count">{typeof count === 'number' ? count : '—'}</span>
    </button>
  )

  const submitFolderModal = async (name: string, st: FolderNameModalState) => {
    if (st.mode === 'create') {
      await folderApi.createFolder(name, st.parentId)
      message.success(t('imageAssets:folders.created'))
    } else {
      await folderApi.renameFolder(st.folderId, name)
      message.success(t('imageAssets:folders.renamed'))
    }
    setFolderModal(null)
  }

  const openCreateFolder = (parentId: string | null) => {
    const parent = parentId ? folderApi.folders.find((f) => f.id === parentId) : null
    setFolderModal({ mode: 'create', parentId, parentName: parent?.name ?? null })
  }

  const confirmDeleteFolder = (folder: { id: string; name: string }) => {
    Modal.confirm({
      title: t('imageAssets:folders.deleteTitle', { name: folder.name }),
      content: t('imageAssets:folders.deleteDescription'),
      okText: t('actions:delete'),
      okButtonProps: { danger: true },
      cancelText: t('actions:cancel'),
      zIndex: 2100,
      onOk: async () => {
        try {
          await folderApi.deleteFolder(folder.id)
          message.success(t('imageAssets:folders.deleted'))
          if (currentFolderId === folder.id) navigateFolder(null)
        } catch (err) {
          message.error((err as SaasApiError).message || t('imageAssets:folders.deleteBlocked'))
        }
      },
    })
  }

  const typeAndFacetFilters = (
    <>
      <div className="iap-filter-group">
        <div className="iap-filter-title">{t(`canvas:${copyNs}.resourceTypes`)}</div>
        {sidebarCategories.map((key) => {
          const count = summaryCountForCategory(summary, key)
          return (
            <button
              key={key}
              type="button"
              className={`iap-filter-item ${activeCategory === key ? 'active' : ''}`}
              onClick={() => onCategoryClick(key)}
            >
              <span className="aap-filter-icon">{TAB_ICON[key]}</span>
              <span>{t(`audioAssets:tabs.${key}`)}</span>
              <span className="iap-filter-count">{count ?? '—'}</span>
            </button>
          )
        })}
      </div>
      {!isVoiceTemplateDomain ? (
        <>
          <div className="iap-filter-group">
            <div className="iap-filter-title">{t('audioAssets:filters.allSources')}</div>
            {(['all', 'UPLOAD', 'AI_GENERATED', 'RECORDED', 'PLATFORM_LIBRARY', 'CANVAS', 'VIDEO_PROJECT', 'IMPORTED'] as const).map(
              (value) => (
                <button
                  key={value}
                  type="button"
                  className={`iap-filter-item ${sourceFilter === value ? 'active' : ''}`}
                  onClick={() => {
                    setSourceFilter(value)
                    setPage(1)
                    stopPlayback()
                  }}
                >
                  <span>
                    {value === 'all' ? t('audioAssets:filters.allSources') : t(`audioAssets:sources.${value}`)}
                  </span>
                </button>
              ),
            )}
          </div>
          <div className="iap-filter-group">
            <div className="iap-filter-title">{t('audioAssets:filters.allOwnership')}</div>
            {(['all', 'WORKSPACE_OWNED', 'PLATFORM_REFERENCE'] as const).map((value) => (
              <button
                key={value}
                type="button"
                className={`iap-filter-item ${ownershipFilter === value ? 'active' : ''}`}
                onClick={() => {
                  setOwnershipFilter(value)
                  setPage(1)
                  stopPlayback()
                }}
              >
                <span>
                  {value === 'all'
                    ? t('audioAssets:filters.allOwnership')
                    : t(`audioAssets:ownership.${value}`)}
                </span>
              </button>
            ))}
          </div>
        </>
      ) : null}
    </>
  )

  const showUploadActions = enableLocalUpload || teamEnabled
  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={null}
      width="min(96vw, 1280px)"
      className="aap-modal"
      rootClassName="tc-audio-asset-picker-modal aap-modal-root"
      destroyOnClose
      closable={false}
      maskClosable
      keyboard
      zIndex={2000}
      data-testid="audio-asset-picker-modal"
      data-aap-display-mode={AUDIO_PICKER_DISPLAY_MODE}
    >
      <div
        className="aap-shell"
        data-testid="audio-asset-picker-shell"
        data-aap-domain={pickerDomain}
      >
        <div className="aap-header">
          <h2 className="aap-title">{modalTitle}</h2>
          <button
            type="button"
            className="aap-close-btn"
            aria-label={t('actions:close')}
            onClick={handleClose}
          >
            <CloseOutlined />
          </button>
        </div>

        <div className="aap-tabs">
          <button
            type="button"
            className={`aap-tab ${tab === 'library' ? 'active' : ''}`}
            onClick={() => setTab('library')}
          >
            {t('imageAssets:picker.tabs.library')}
          </button>
          <button
            type="button"
            className="aap-tab"
            disabled
            title={t('imageAssets:picker.tabs.comingSoon')}
          >
            {t('imageAssets:picker.tabs.externalLink')}
          </button>
        </div>

        {tab === 'library' && (
          <>
            <div className="aap-toolbar">
              <Input
                className="aap-search"
                allowClear
                prefix={<SearchOutlined />}
                placeholder={t(`canvas:${copyNs}.searchPlaceholder`)}
                value={searchDraft}
                onChange={(e) => onSearchChange(e.target.value)}
                data-testid="audio-asset-picker-search"
              />
              {!isVoiceTemplateDomain && !teamEnabled ? (
                <>
                  <Select
                    className="aap-filter-select"
                    value={sourceFilter}
                    onChange={(v) => {
                      setSourceFilter(v)
                      setPage(1)
                      stopPlayback()
                    }}
                    options={[
                      { value: 'all', label: t('audioAssets:filters.allSources') },
                      { value: 'UPLOAD', label: t('audioAssets:sources.UPLOAD') },
                      { value: 'AI_GENERATED', label: t('audioAssets:sources.AI_GENERATED') },
                      { value: 'RECORDED', label: t('audioAssets:sources.RECORDED') },
                      { value: 'PLATFORM_LIBRARY', label: t('audioAssets:sources.PLATFORM_LIBRARY') },
                      { value: 'CANVAS', label: t('audioAssets:sources.CANVAS') },
                      { value: 'VIDEO_PROJECT', label: t('audioAssets:sources.VIDEO_PROJECT') },
                      { value: 'IMPORTED', label: t('audioAssets:sources.IMPORTED') },
                    ]}
                    data-testid="audio-asset-picker-source-filter"
                  />
                  <Select
                    className="aap-filter-select"
                    value={ownershipFilter}
                    onChange={(v) => {
                      setOwnershipFilter(v)
                      setPage(1)
                      stopPlayback()
                    }}
                    options={[
                      { value: 'all', label: t('audioAssets:filters.allOwnership') },
                      { value: 'WORKSPACE_OWNED', label: t('audioAssets:ownership.WORKSPACE_OWNED') },
                      { value: 'PLATFORM_REFERENCE', label: t('audioAssets:ownership.PLATFORM_REFERENCE') },
                    ]}
                    data-testid="audio-asset-picker-ownership-filter"
                  />
                </>
              ) : null}
              {teamEnabled ? (
                <Popover
                  trigger="click"
                  placement="bottomLeft"
                  overlayClassName="iap-filter-popover"
                  zIndex={2100}
                  content={
                    <div className="iap-filter-popover-body" data-testid="aap-filter-popover">
                      {typeAndFacetFilters}
                    </div>
                  }
                >
                  <Button icon={<FilterOutlined />} data-testid="aap-filter-btn">
                    {t('imageAssets:picker.filters.filter')}
                  </Button>
                </Popover>
              ) : null}
              <Select
                className="aap-filter-select"
                value={sort}
                onChange={(v) => {
                  setSort(v)
                  setPage(1)
                  stopPlayback()
                }}
                options={[
                  { value: 'recent', label: t('audioAssets:filters.sortRecent') },
                  { value: 'oldest', label: t('audioAssets:filters.sortOldest') },
                  { value: 'name', label: t('audioAssets:filters.sortName') },
                  { value: 'duration', label: t('audioAssets:filters.sortDuration') },
                ]}
                data-testid="audio-asset-picker-sort"
              />
              <Button
                icon={<ReloadOutlined />}
                onClick={() => void loadGallery()}
                disabled={loading}
                aria-label={t('audioAssets:filters.refresh')}
              />
            </div>

            <div
              className={`aap-body${teamEnabled ? ' is-team' : ''}`}
              data-aap-team-mode={teamEnabled ? 'true' : 'false'}
            >
              {teamEnabled ? (
                <aside className="iap-sidebar iap-team-sidebar" data-testid="aap-team-sidebar">
                  <div className="iap-team-ws">
                    <span className="iap-team-ws-avatar" aria-hidden>
                      {(teamWorkspaceName.trim()[0] || 'W').toUpperCase()}
                    </span>
                    <span className="iap-team-ws-name" title={teamWorkspaceName}>
                      {teamWorkspaceName}
                    </span>
                    <span className="iap-team-badge" data-testid="aap-team-badge">
                      {t('imageAssets:folders.scope.teamModeBadge')}
                    </span>
                  </div>
                  <div className="iap-team-hint" data-testid="aap-team-hint">
                    {teamIsManager
                      ? t('audioAssets:folders.scope.managerHint')
                      : t('audioAssets:folders.scope.memberHint')}
                  </div>
                  <div className="iap-filter-group">
                    <div className="iap-filter-title">{t('imageAssets:folders.scope.myLibrary')}</div>
                    {scopeEntry('my_all', t('audioAssets:folders.scope.allAudio'), scopeCounts?.all)}
                    {scopeEntry('my_created', t('imageAssets:folders.scope.myCreated'), scopeCounts?.mine)}
                  </div>
                  <div className="iap-filter-group">
                    <div className="iap-filter-title">{t('imageAssets:folders.scope.teamShared')}</div>
                    {scopeEntry('team_all', t('audioAssets:folders.scope.allAudio'), scopeCounts?.shared)}
                    {scopeEntry(
                      'team_members',
                      t('imageAssets:folders.scope.teamMembers'),
                      scopeCounts?.shared_by_others,
                    )}
                    {scopeEntry('unsorted', t('audioAssets:folders.scope.unsortedAudio'), scopeCounts?.unsorted)}
                  </div>
                  <div className="iap-team-folders">
                    <ImageFolderTree
                      folders={folderApi.folders}
                      selectedFolderId={currentFolderId}
                      onSelect={(id) => navigateFolder(id)}
                      canManage={folderApi.canManage}
                      onCreate={openCreateFolder}
                      onRename={(f) => setFolderModal({ mode: 'rename', folderId: f.id, name: f.name })}
                      onDelete={confirmDeleteFolder}
                      testIdPrefix="aap-ift"
                    />
                    {folderApi.status === 'error' ? (
                      <div className="iap-team-folders-error" role="status" data-testid="aap-folders-error">
                        <span>{t('imageAssets:folders.loadFailed')}</span>
                        <Button size="small" type="link" onClick={() => void folderApi.reload()}>
                          {t('imageAssets:folders.retry')}
                        </Button>
                      </div>
                    ) : null}
                  </div>
                </aside>
              ) : (
                <aside className="aap-sidebar" data-testid="audio-asset-picker-sidebar">
                  <div className="aap-filter-group">
                    <div className="aap-filter-title">
                      {t(`canvas:${isVoiceTemplateDomain ? 'voiceTemplatePicker' : 'audioAssetPicker'}.resourceTypes`)}
                    </div>
                    {sidebarCategories.map((key) => {
                      const count = summaryCountForCategory(summary, key)
                      return (
                        <button
                          key={key}
                          type="button"
                          className={`aap-filter-item ${activeCategory === key ? 'active' : ''}`}
                          data-testid={`audio-asset-picker-category-${key}`}
                          onClick={() => onCategoryClick(key)}
                        >
                          <span className="aap-filter-icon">{TAB_ICON[key]}</span>
                          <span className="aap-filter-label">{t(`audioAssets:tabs.${key}`)}</span>
                          <span className="aap-filter-count">{count ?? '—'}</span>
                        </button>
                      )
                    })}
                  </div>
                </aside>
              )}

              <section className="aap-gallery">
                {teamEnabled ? (
                  <div className="aap-team-head" data-testid="aap-team-head">
                    <ImageFolderBreadcrumb
                      folders={folderApi.folders}
                      currentFolderId={currentFolderId}
                      onNavigate={navigateFolder}
                      rootLabel={scopeRootLabel}
                      testIdPrefix="aap-crumb"
                    />
                    <span className="aap-team-head-actions">
                      {folderApi.canManage ? (
                        <Button
                          size="small"
                          icon={<FolderAddOutlined />}
                          onClick={() => openCreateFolder(currentFolderId)}
                          data-testid="aap-new-folder"
                        >
                          {t('imageAssets:folders.createHere')}
                        </Button>
                      ) : null}
                      <Button
                        size="small"
                        icon={<UploadOutlined />}
                        onClick={handleLocalUploadPick}
                        disabled={!mobileUploadReady || uploading}
                        data-testid="aap-upload-here"
                      >
                        {t('imageAssets:picker.upload.tileLabel')}
                      </Button>
                    </span>
                  </div>
                ) : null}
                <div className="aap-gallery-scroll">
                  {showUploadActions && !isVoiceTemplateDomain ? (
                    <div className="aap-upload-row" data-testid="audio-asset-picker-upload-actions">
                      <AssetUploadActionTile
                        layout="list"
                        mobileLabel={t('canvas:mediaCard.picker.upload.mobile')}
                        localLabel={t('canvas:audioAssetPicker.localUpload')}
                        tileLabel={t('canvas:mediaCard.picker.upload.tileLabel')}
                        onMobileClick={openMobileCapture}
                        onLocalClick={handleLocalUploadPick}
                        mobileDisabled={!mobileUploadReady || uploading}
                        localDisabled={uploading}
                        testIdPrefix="audio-asset-picker"
                        markerPrefix="aap"
                      />
                    </div>
                  ) : null}
                  {loading && items.length === 0 ? (
                    <div className="aap-skeleton-list" data-testid="audio-asset-picker-loading">
                      {Array.from({ length: 10 }).map((_, i) => (
                        <div key={i} className="aap-skeleton-row" />
                      ))}
                    </div>
                  ) : error ? (
                    <div className="aap-error" data-testid="audio-asset-picker-error">
                      <p>{error || t(`canvas:${copyNs}.loadFailed`)}</p>
                      <Button type="primary" onClick={() => void loadGallery()}>
                        {t(`canvas:${copyNs}.retry`)}
                      </Button>
                    </div>
                  ) : items.length === 0 ? (
                    <div className="aap-empty" data-testid="audio-asset-picker-empty">
                      <Empty description={emptyDescription} />
                    </div>
                  ) : (
                    <table className="aap-table" data-testid="audio-asset-picker-list">
                      <thead>
                        <tr>
                          <th className="aap-th-check" />
                          <th className="aap-th-play">{t('canvas:audioAssetPicker.columnPreview')}</th>
                          <th className="aap-th-name">{t('audioAssets:table.name')}</th>
                          <th className="aap-th-type">{t('audioAssets:table.type')}</th>
                          <th className="aap-th-duration">{t('audioAssets:table.duration')}</th>
                          <th className="aap-th-source">{t('audioAssets:table.source')}</th>
                          <th className="aap-th-created">{t('audioAssets:table.createdAt')}</th>
                          {teamEnabled ? (
                            <th className="aap-th-creator">{t('imageAssets:team.creator.label')}</th>
                          ) : null}
                          {teamEnabled ? (
                            <th className="aap-th-share">{t('imageAssets:team.share.column')}</th>
                          ) : null}
                          <th className="aap-th-ops" />
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((item) => {
                          const selectable = isAudioAssetSelectable(item)
                          const isSelected = selectedId === item.id
                          const isActive = activeAssetId === item.id
                          const isPlaying = playingId === item.id
                          const failTooltipKey = failedVoiceTemplateTooltipKey(item)
                          const failTooltip = failTooltipKey ? t(failTooltipKey) : undefined
                          return (
                            <tr
                              key={item.id}
                              className={`${isSelected ? 'is-selected' : ''}${isActive ? ' is-active' : ''}${!selectable ? ' is-disabled' : ''}`}
                              data-testid="audio-asset-picker-row"
                              data-asset-id={item.id}
                              data-usable={selectable ? 'yes' : 'no'}
                              title={failTooltip}
                              onClick={() => selectRow(item)}
                              onDoubleClick={() => {
                                if (!selectable) return
                                stopPlayback()
                                onConfirm(item)
                              }}
                            >
                              <td className="aap-td-check" onClick={(e) => e.stopPropagation()}>
                                <Checkbox
                                  checked={isSelected}
                                  disabled={!selectable}
                                  onChange={() => selectRow(item)}
                                />
                              </td>
                              <td className="aap-td-play">
                                <div className="aap-name-cell">
                                  <button
                                    type="button"
                                    className="aap-play-btn nodrag"
                                    aria-label={t('canvas:generateAudio.selector.previewPlay')}
                                    disabled={!item.has_preview_audio || !item.playback_url}
                                    onClick={(e) => togglePlay(item, e)}
                                  >
                                    {isPlaying ? <PauseCircleFilled /> : <PlayCircleFilled />}
                                  </button>
                                  <span className="aap-waveform">
                                    <AudioWaveformPlaceholder
                                      variant={item.has_preview_audio ? 'audio' : 'voiceTemplate'}
                                      width={72}
                                      height={20}
                                    />
                                  </span>
                                </div>
                              </td>
                              <td className="aap-td-name">
                                <span className="aap-name-text" title={item.name}>{item.name}</span>
                              </td>
                              <td className="aap-td-type">
                                <span className={`aap-badge aap-badge-${item.presentation_type.toLowerCase()}`}>
                                  {t(`audioAssets:tabs.${item.presentation_type.toLowerCase()}`)}
                                </span>
                                {item.status ? (
                                  <span className={`aap-status aap-status-${item.status.toLowerCase()}`}>
                                    {t(`audioAssets:status.${item.status}`, item.status)}
                                  </span>
                                ) : null}
                              </td>
                              <td className="aap-td-mono">
                                {formatDurationMs(item.duration_ms ?? item.preview_duration_ms ?? null)}
                              </td>
                              <td className="aap-td-source">
                                <span className="aap-source-chip">{t(`audioAssets:sources.${item.source}`)}</span>
                              </td>
                              <td className="aap-td-time aap-td-created">
                                {formatCreatedAt(item.updated_at || item.created_at)}
                              </td>
                              {teamEnabled ? (
                                <td className="aap-td-creator">
                                  {item.creator?.is_me
                                    ? t('audioAssets:folders.card.me')
                                    : item.creator?.display_name || '—'}
                                </td>
                              ) : null}
                              {teamEnabled ? (
                                <td className="aap-td-share">
                                  <span
                                    className={`aap-share-chip${item.visibility === 'private' ? ' is-private' : ''}`}
                                  >
                                    {item.visibility === 'private'
                                      ? t('audioAssets:folders.card.private')
                                      : t('audioAssets:folders.card.teamShared')}
                                  </span>
                                </td>
                              ) : null}
                              <td className="aap-td-ops" onClick={(e) => e.stopPropagation()}>
                                <Dropdown
                                  menu={{
                                    items: [
                                      {
                                        key: 'preview',
                                        label: t('canvas:audioAssetPicker.rowPreview'),
                                        disabled: !item.playback_url,
                                      },
                                    ],
                                    onClick: ({ key }) => {
                                      if (key === 'preview') togglePlay(item)
                                    },
                                  }}
                                  trigger={['click']}
                                >
                                  <Button type="text" size="small" icon={<MoreOutlined />} />
                                </Dropdown>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </section>

              <aside className="aap-detail" data-testid="audio-asset-picker-detail">
                {!detailItem ? (
                  <Empty
                    description={t('canvas:audioAssetPicker.selectForDetail')}
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                ) : (
                  <>
                    <div className="aap-detail-player">
                      <button
                        type="button"
                        className="aap-detail-play"
                        disabled={!detailItem.has_preview_audio || !detailItem.playback_url}
                        onClick={() => togglePlay(detailItem)}
                      >
                        {playingId === detailItem.id ? <PauseCircleFilled /> : <PlayCircleFilled />}
                      </button>
                      <div className="aap-detail-wave-wrap">
                        <AudioWaveformPlaceholder
                          variant={detailItem.has_preview_audio ? 'audio' : 'voiceTemplate'}
                          width={180}
                          height={28}
                        />
                        {playingId === detailItem.id && playbackDuration > 0 ? (
                          <input
                            type="range"
                            className="aap-detail-progress"
                            min={0}
                            max={playbackDuration}
                            value={playbackProgress}
                            onChange={(e) => {
                              const el = audioElRef.current
                              if (!el) return
                              const next = Number(e.target.value)
                              el.currentTime = next
                              setPlaybackProgress(next)
                            }}
                          />
                        ) : null}
                        <div className="aap-detail-time">
                          {formatDurationMs(Math.round(playbackProgress * 1000))} /{' '}
                          {formatDurationMs(
                            playbackDuration > 0
                              ? Math.round(playbackDuration * 1000)
                              : detailItem.duration_ms ?? detailItem.preview_duration_ms ?? null,
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="aap-detail-name">{detailItem.name}</div>
                    <div className="aap-detail-badges">
                      <span className={`aap-badge aap-badge-${detailItem.presentation_type.toLowerCase()}`}>
                        {t(`audioAssets:tabs.${detailItem.presentation_type.toLowerCase()}`)}
                      </span>
                      {detailItem.status ? (
                        <span className={`aap-status aap-status-${detailItem.status.toLowerCase()}`}>
                          {t(`audioAssets:status.${detailItem.status}`, detailItem.status)}
                        </span>
                      ) : null}
                    </div>
                    <div className="aap-detail-section">
                      <h4>{t('canvas:audioAssetPicker.detailBasic')}</h4>
                      <div className="aap-kv">
                        <span>{t('audioAssets:table.duration')}</span>
                        <span>
                          {formatDurationMs(detailItem.duration_ms ?? detailItem.preview_duration_ms ?? null)}
                        </span>
                        <span>{t('audioAssets:table.source')}</span>
                        <span>{t(`audioAssets:sources.${detailItem.source}`)}</span>
                        <span>{t('audioAssets:table.createdAt')}</span>
                        <span>{formatCreatedAt(detailItem.updated_at || detailItem.created_at)}</span>
                        {teamEnabled && detailItem.creator ? (
                          <>
                            <span>{t('imageAssets:team.creator.label')}</span>
                            <span>
                              {detailItem.creator.is_me
                                ? t('audioAssets:folders.card.me')
                                : detailItem.creator.display_name}
                            </span>
                          </>
                        ) : null}
                        {teamEnabled && detailItem.visibility ? (
                          <>
                            <span>{t('imageAssets:team.share.column')}</span>
                            <span>
                              {detailItem.visibility === 'private'
                                ? t('audioAssets:folders.card.private')
                                : t('audioAssets:folders.card.teamShared')}
                            </span>
                          </>
                        ) : null}
                        <span>{t('audioAssets:table.usageCount')}</span>
                        <span>{detailItem.usage_count ?? t('audioAssets:table.noUsage')}</span>
                        <span>{t('audioAssets:table.tags')}</span>
                        <span>
                          {detailItem.tags.length > 0 ? detailItem.tags.join(', ') : t('audioAssets:table.noTags')}
                        </span>
                      </div>
                    </div>
                    {(detailItem.description || detailItem.voice_brief) && (
                      <div className="aap-detail-desc">
                        <h4>{t('canvas:audioAssetPicker.detailDescription')}</h4>
                        <p>{detailItem.description || detailItem.voice_brief}</p>
                      </div>
                    )}
                  </>
                )}
              </aside>
            </div>

            <div className="aap-footer">
              <div className="aap-footer-left" data-testid="audio-asset-picker-selection-count">
                {t(`canvas:${copyNs}.selectedCount`, { count: selectedId ? 1 : 0 })}
              </div>
              <div className="aap-footer-center">
                <Pagination
                  size="small"
                  current={page}
                  pageSize={pageSize}
                  total={total}
                  showSizeChanger
                  pageSizeOptions={PAGE_SIZE_OPTIONS.map(String)}
                  onChange={(nextPage, nextSize) => {
                    setPage(nextPage)
                    setPageSize(nextSize)
                    stopPlayback()
                  }}
                  showTotal={() => `${page} / ${totalPages}`}
                />
              </div>
              <div className="aap-footer-right">
                <Button onClick={handleClose}>{t('actions:cancel')}</Button>
                <Button
                  type="primary"
                  disabled={!selected || !isAudioAssetSelectable(selected)}
                  data-testid="audio-asset-picker-confirm"
                  onClick={handleConfirm}
                >
                  {confirmLabel}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept={buildAudioUploadFileInputAccept()}
        hidden
        data-aap-upload-input="true"
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          const file = e.target.files?.[0]
          e.target.value = ''
          if (file) void handleLocalUploadFile(file)
        }}
      />
      <FolderNameModal
        state={folderModal}
        onCancel={() => setFolderModal(null)}
        onSubmit={submitFolderModal}
        testIdPrefix="aap-folder"
      />
      <MobileCaptureQrModal
        open={mobileCaptureOpen}
        workspaceId={workspaceId}
        targetFlow="audio_asset_library"
        audioPresentationType={mobileAudioPresentationType}
        uploadHintKey="mobileCapture.uploadHintAudioAssetLibrary"
        onClose={() => setMobileCaptureOpen(false)}
        onSessionMetaChange={setMobileSessionMeta}
      />
      <audio
        ref={audioElRef}
        className="aap-hidden-audio"
        onEnded={() => {
          setPlayingId(null)
          setPlaybackProgress(0)
        }}
        onPause={() => {
          if (audioElRef.current?.paused) setPlayingId(null)
        }}
        onTimeUpdate={() => {
          const el = audioElRef.current
          if (!el) return
          setPlaybackProgress(el.currentTime)
          setPlaybackDuration(el.duration || 0)
        }}
        onLoadedMetadata={() => {
          const el = audioElRef.current
          if (!el) return
          setPlaybackDuration(el.duration || 0)
        }}
      />
    </Modal>
  )
}
